import { Base } from "../base";
import { CipherSuite, DomainCertChallengeType, DomainDnsMode, DomainPortProtocol, DomainTlsVersion } from "./common";
import { CreateDomainPortCors, CreateDomainPortRouteHeaders } from "./create";

export interface DomainPortRouteMirror {
  workloadLink: string;
  percent: number;
  port?: number;
}

export interface DomainPortRoute {
  prefix: string;
  replacePrefix?: string;
  regex?: string;
  workloadLink: string;
  port?: number;
  hostPrefix?: string;
  hostRegex?: string;
  headers?: CreateDomainPortRouteHeaders;
  replica?: number;
  mirror?: DomainPortRouteMirror[];
}

export interface DomainPortTls {
  minProtocolVersion: DomainTlsVersion;
  cipherSuites: CipherSuite[];
  clientCertificate?: {
    secretLink?: string;
  };
  serverCertificate?: {
    secretLink?: string;
  };
}

export interface DomainPort {
  number: number;
  protocol: DomainPortProtocol;
  routes?: DomainPortRoute[];
  cors?: CreateDomainPortCors;
  tls?: DomainPortTls;
}

export interface DomainSpec {
  dnsMode: DomainDnsMode;
  gvcLink?: string;
  certChallengeType: DomainCertChallengeType;
  workloadLink?: string;
  acceptAllHosts: boolean;
  acceptAllSubdomains: boolean;
  ports: DomainPort[];
}

export interface DomainDnsConfigRecord {
  type: string;
  ttl: number;
  host: string;
  value: string;
}

export interface DomainStatusEndpoint {
  url: string;
  workloadLink?: string;
}

export interface DomainStatusLocation {
  name: string;
  certificateStatus?: "initializing" | "ready" | "pendingDnsConfig" | "pendingCertificate" | "ignored";
}

export interface DomainStatus {
  endpoints?: DomainStatusEndpoint[];
  status?:
    | "initializing"
    | "ready"
    | "pendingDnsConfig"
    | "pendingCertificate"
    | "usedByGvc"
    | "warning"
    | "created"
    | "updated"
    | "deleted"
    | "errored"
    | "ignored";
  warning?: string;
  locations?: DomainStatusLocation[];
  fingerprint?: string;
  dnsConfig?: DomainDnsConfigRecord[];
}

export interface Domain extends Base {
  spec: DomainSpec;
  status?: DomainStatus;
}

const allowedCiphersForOldTLSVersions: string[] = [
  "DES-CBC3-SHA",
  "ECDHE-RSA-AES128-SHA",
  "ECDHE-RSA-AES256-SHA",
  "AES128-SHA",
  "AES256-SHA",
];

const allowedCiphersNew: string[] = [
  "ECDHE-ECDSA-AES256-GCM-SHA384",
  "ECDHE-ECDSA-CHACHA20-POLY1305",
  "ECDHE-ECDSA-AES128-GCM-SHA256",
  "ECDHE-RSA-AES256-GCM-SHA384",
  "ECDHE-RSA-CHACHA20-POLY1305",
  "ECDHE-RSA-AES128-GCM-SHA256",
  "AES256-GCM-SHA384",
  "AES128-GCM-SHA256",
];

const oldNewCipherMap: any = {
  TLS_RSA_WITH_AES_256_GCM_SHA384: "ECDHE-RSA-AES256-GCM-SHA384",
  TLS_RSA_WITH_AES_128_GCM_SHA256: "ECDHE-RSA-AES128-GCM-SHA256",
  TLS_ECDHE_RSA_WITH_CHACHA20_POLY1305_SHA256: "ECDHE-RSA-CHACHA20-POLY1305",
  TLS_ECDHE_RSA_WITH_AES_256_GCM_SHA384: "ECDHE-RSA-AES256-GCM-SHA384",
  TLS_ECDHE_RSA_WITH_AES_128_GCM_SHA256: "ECDHE-RSA-AES128-GCM-SHA256",
  TLS_CHACHA20_POLY1305_SHA256: "ECDHE-RSA-CHACHA20-POLY1305",
  TLS_AES_256_GCM_SHA384: "AES256-GCM-SHA384",
  TLS_AES_128_GCM_SHA256: "AES128-GCM-SHA256",
};

export const defaultCiphers: string[] = allowedCiphersNew;

export const allowedCiphers: string[] = [...allowedCiphersNew, ...allowedCiphersForOldTLSVersions];

export function convertCiphers(ciphers: string[]) {
  const _ciphers = JSON.parse(JSON.stringify(ciphers));
  const newCiphers: string[] = [];
  for (let cipher of _ciphers) {
    if (oldNewCipherMap[cipher]) {
      newCiphers.push(oldNewCipherMap[cipher]);
    } else {
      newCiphers.push(cipher);
    }
  }
  return newCiphers;
}
